import React, { useCallback, useMemo, useState } from "react";
import { isNil, omit, round } from "lodash";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Theme } from "@mui/material/styles";
import { BaseRow, CellProps, ColumnMeta, HeaderCellProps } from "@shared/components/DataGrid/models";
import { Currency, CurrencySymbol } from "@shared/models/currency";
import { Checkbox } from "@shared/components/Checkbox";
import { ReactComponent as ActionFlagIcon } from "@assets/svg/flag_icon.svg";
import { ReactComponent as DownloadIcon } from "@assets/svg/download_icon.svg";
import { ReactComponent as VideoIcon } from "@assets/svg/video_icon.svg";
import { ReactComponent as LinkIcon } from "@assets/svg/link.svg";
import { ReactComponent as CheckIcon } from "@assets/svg/check_in_circle.svg";
import NumberUtils from "@shared/utils/NumberUtils";
import { Button } from "@shared/components/Button";

type StyleProps = Pick<
  HeaderCellProps<any>,
  "align" | "width" | "flex" | "type" | "minWidth" | "pinned" | "textWrap"
> & {
  currency?: Currency;
  interactive: boolean;
  value: boolean;
};

const isNumeric = (type: ColumnMeta<any>["type"]) => ["currency", "number"].some((t) => t === type);

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  cell: {
    alignSelf: "center",
    padding: theme.spacing(0, 2.5),
    position: ({ pinned }) => (pinned ? "sticky" : "relative"),
    left: ({ pinned }) => (pinned ? 0 : "auto"),
    zIndex: ({ pinned }) => (pinned ? 1 : 0),
    backgroundColor: "inherit",
    whiteSpace: ({ textWrap }) => (textWrap ? "normal" : "nowrap"),
    overflow: "hidden",
    textOverflow: "ellipsis",
    textWrap: ({ textWrap }) => (textWrap ? "wrap" : "nowrap"),
    textAlign: ({ align }) => align,
    width: ({ width }) => width,
    minWidth: ({ minWidth }) => minWidth,
    flex: ({ flex }) => flex ?? "initial",
    fontFamily: ({ type }) => (isNumeric(type) ? "Segoe" : "inherit"),
    fontWeight: ({ type }) => (isNumeric(type) ? theme.typography.fontWeightMedium : "inherit"),
    transition: "color ease-in .15s",
    // Nudge only the action-flag column down slightly so the flag visually
    // aligns with the checkbox and document icon in DataRooms.
    marginTop: ({ type }) => (type === "actionFlag" ? theme.spacing(1) : 0),

    "&:hover": {
      cursor: ({ interactive }) => (interactive ? "pointer" : "default"),
      textDecoration: ({ interactive }) => (interactive ? "underline" : "none"),
      color: ({ interactive }) => (interactive ? theme.palette.info.main : theme.palette.primary.main),
    },
  },
  currency: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.grey[600],
    marginRight: theme.spacing(1),
  },
  readIndicator: {
    width: 13,
    height: 13,
    // Match the existing DataRooms/Documents unread indicator blue.
    background: theme.palette.info.main,
    borderRadius: "50%",
  },
  actionFlag: {
    width: 13,
    height: 13,
    cursor: "pointer",
    color: ({ value }) => (value ? theme.palette.primary.main : theme.palette.grey[500]),
  },
  downloadIcon: {
    width: 14,
    height: 18,
  },
  videoIcon: {
    display: "block",
    width: 20,
    height: 12,
  },
  title: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 18,
    padding: theme.spacing(0, 2.5, 0, 0),

    fontWeight: theme.typography.fontWeightMedium,
  },
  author: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: theme.spacing(0, 2.5, 0, 0),
    fontWeight: theme.typography.fontWeightRegular,
  },
  linkIcon: {
    width: 16,
    height: 16,
    cursor: "pointer",
    color: theme.palette.primary.main,
  },
  linkIconCopied: {
    width: 16,
    height: 16,
    cursor: "pointer",
    color: theme.palette.success.main,
  },
}));

const Cell = <D extends BaseRow>(props: CellProps<D>) => {
  const classes = useStyles({
    pinned: props.column.pinned ?? false,
    align: props.column.align ?? "right",
    width: props.column.width ?? "center",
    flex: props.column.flex ?? "initial",
    type: props.column.type ?? "string",
    currency: props.row.original.rowMeta?.currency,
    interactive: Boolean(props.column.onClick) && !props.row.original.rowMeta?.nonClickableRow,
    value: props.value,
    minWidth: props.column.minWidth,
    textWrap: props.column.textWrap ?? "",
  });

  const getLinkTooltipText = useCallback(
    (row: CellProps<D>["row"]) => (row.original.rowMeta?.type === "Video" ? "Copy video link" : "Copy document link"),
    []
  );
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isLinkTooltipOpen, setIsLinkTooltipOpen] = useState(false);

  const [linkTooltipText, setLinkTooltipText] = useState(() => getLinkTooltipText(props.row));

  const handleClick = useCallback(() => {
    if (props.column.onClick) {
      props.column.onClick(props.row);
    }
  }, [props.column, props.row]);

  const content = useMemo(() => {
    const cellValue = props.row.values[props.column.id];
    //author
    const getCurrencyCellValue = (): string => {
      if (isNil(cellValue)) return "";
      if (!props.column.numberFormat) return cellValue;

      return NumberUtils.formatNumber(round(cellValue, props.column.currencyFractionDigits), props.column.numberFormat);
    };

    const getAuthorCellValue = (): string[] => {
      if (isNil(cellValue)) return [];
      // if (!props.column.numberFormat) return cellValue;
      const insightTitle = (cellValue as string).split("__?");

      return insightTitle;
    };

    if (props.row.original.rowMeta?.cellRenderers?.[props.column.id]) {
      return props.row.original.rowMeta?.cellRenderers?.[props.column.id]?.(props);
    }

    switch (props.column.type) {
      case "readIndicator":
        return !cellValue ? <div data-testid="read-indicator" className={classes.readIndicator} /> : null;
      case "checkbox":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 1,
            }}
          >
            {props.row.original.rowMeta?.type === "Video" ? (
              <VideoIcon className={classes.videoIcon} />
            ) : props.row.original.rowMeta?.type === "Readonly" ||
              props.row.original.rowMeta?.type === "HighlightedReadonly" ? (
              <Checkbox checked={false} isReadonly={true} />
            ) : (
              <Checkbox checked={props.row.isSelected} onChange={props.row.toggleRowSelected} isReadonly={false} />
            )}
          </Box>
        );
      case "actionFlag":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 1,
            }}
          >
            <ActionFlagIcon className={classes.actionFlag} role="actionFlag" />
          </Box>
        );
      case "currency": {
        const value = getCurrencyCellValue();

        return (
          <Box>
            {Boolean(value) && (
              <span data-testid="currency" className={classes.currency}>
                {CurrencySymbol[props.row.original.rowMeta!.currency!]}
              </span>
            )}
            {value}
          </Box>
        );
      }
      case "author": {
        const value = getAuthorCellValue();

        return (
          <div>
            {value.length > 0 && (
              <div>
                <Box component="div" className={classes.title}>
                  {value[0]}
                </Box>
                <Box component="div" className={classes.author}>
                  {value[1]}
                </Box>
              </div>
            )}
          </div>
        );
      }
      case "download":
        return props.row.original["isLoading"] ? (
          <CircularProgress size={14} />
        ) : (
          <DownloadIcon className={classes.downloadIcon} role="download" data-testid="download-icon" />
        );
      case "learnMore":
        return (
          <Button size={"small"} shape="rounded" onClick={() => false}>
            Read More
          </Button>
        );
      case "link": {
        const handleLinkClick: React.MouseEventHandler<HTMLSpanElement> = (event) => {
          event.stopPropagation();

          if (props.column.onClick) {
            props.column.onClick(props.row);
          }

          setIsLinkCopied(true);
          setLinkTooltipText("Link copied");
          setIsLinkTooltipOpen(true);

          window.setTimeout(() => {
            setIsLinkCopied(false);
            setIsLinkTooltipOpen(false);
          }, 1000);
        };

        const handleLinkMouseEnter: React.MouseEventHandler<HTMLSpanElement> = () => {
          setLinkTooltipText(getLinkTooltipText(props.row));
          setIsLinkTooltipOpen(true);
        };

        const handleLinkMouseLeave: React.MouseEventHandler<HTMLSpanElement> = () => {
          setIsLinkTooltipOpen(false);
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 1,
            }}
          >
            <Tooltip
              title={linkTooltipText}
              open={isLinkTooltipOpen}
              disableHoverListener
              disableFocusListener
              disableTouchListener
            >
              <Box
                component="span"
                onClick={handleLinkClick}
                onMouseEnter={handleLinkMouseEnter}
                onMouseLeave={handleLinkMouseLeave}
              >
                {isLinkCopied ? (
                  <CheckIcon className={classes.linkIconCopied} />
                ) : (
                  <LinkIcon className={classes.linkIcon} />
                )}
              </Box>
            </Tooltip>
          </Box>
        );
      }
      default:
        return props.render("Cell");
    }
  }, [
    props,
    classes.readIndicator,
    classes.author,
    classes.title,
    classes.videoIcon,
    classes.actionFlag,
    classes.downloadIcon,
    classes.currency,
    classes.linkIcon,
    classes.linkIconCopied,
    getLinkTooltipText,
    isLinkCopied,
    isLinkTooltipOpen,
    linkTooltipText,
  ]);

  const cellProps = omit(props.getCellProps(), "style");
  const rootOnClick = props.column.type === "link" ? undefined : handleClick;

  return (
    <div {...cellProps} className={classes.cell} onClick={rootOnClick} data-testid="datagrid-cell">
      {props.column.type === "link" ? (
        content
      ) : props.column.withTooltip && content ? (
        <Tooltip title={content}>
          <Typography variant="bodySubhead3" fontSize={14}>
            {content}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="bodySubhead3" fontSize={14}>
          {content}
        </Typography>
      )}
    </div>
  );
};

export default Cell;
